import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, Tag, ArrowLeft, Ticket } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { eventInfo, images } from '../data/mock';
import { blogPosts } from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const openTickets = () => window.open(eventInfo.ticketUrl, '_blank', 'noopener,noreferrer');
  const canonical = `https://www.alohatequilafest.com/blog/${post.slug}`;

  const renderBlock = (block, i) => {
    if (block.type === 'h2') {
      return (
        <h2 key={i} className="text-2xl sm:text-3xl font-bold text-white mt-10 mb-4">
          {block.text}
        </h2>
      );
    }
    if (block.type === 'p') {
      return (
        <p key={i} className="text-white/80 leading-relaxed text-base sm:text-lg mb-4">
          {block.text}
        </p>
      );
    }
    if (block.type === 'list') {
      return (
        <ul key={i} className="space-y-2 mb-6 list-none pl-0">
          {block.items.map((item, j) => (
            <li key={j} className="flex gap-3 text-white/80 leading-relaxed text-base sm:text-lg">
              <span className="text-pink-400 flex-shrink-0 mt-1.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-950" data-testid={`blog-post-${post.slug}`}>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.coverImage} />
        <meta property="article:published_time" content={post.publishedAt} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.metaDescription,
            url: canonical,
            mainEntityOfPage: canonical,
            image: [post.coverImage],
            datePublished: post.publishedAt,
            dateModified: post.publishedAt,
            author: { '@type': 'Organization', name: 'Aloha Tequila & Arts Festival' },
            publisher: {
              '@type': 'Organization',
              name: 'Aloha Tequila & Arts Festival',
              logo: { '@type': 'ImageObject', url: images.logo }
            },
            keywords: post.keywords
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.alohatequilafest.com/blog' },
            { '@type': 'ListItem', position: 3, name: post.title, item: canonical }
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-28 sm:pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-4">
          <Link
            to="/blog"
            data-testid="blog-back-to-blog-link"
            className="inline-flex items-center gap-2 text-pink-400 hover:text-orange-400 transition-colors text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to blog
          </Link>

          {/* Tag + read time */}
          <div className="flex items-center gap-3 text-white/60 text-xs mb-4">
            <span className="inline-flex items-center gap-1 text-pink-400">
              <Tag className="w-3 h-3" />
              {post.tag}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
            <span>·</span>
            <span>{new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 leading-tight mb-8">
            {post.title}
          </h1>

          <div className="aspect-[16/10] rounded-3xl overflow-hidden mb-10 border border-white/10">
            <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover" />
          </div>

          {/* Body */}
          <div className="article-body">
            {post.blocks.map(renderBlock)}
          </div>

          {/* CTA */}
          <div className="mt-14 bg-gradient-to-br from-pink-500/15 via-orange-500/15 to-yellow-500/10 border border-pink-500/40 rounded-3xl p-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">
              See you on June 20, 2026
            </h2>
            <p className="text-white/80 mb-6">
              International Market Place, Waikiki · 5–9 PM · 21+
            </p>
            <Button
              onClick={openTickets}
              data-testid="blog-cta-tickets-button"
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white text-base font-bold rounded-full px-7 py-5 h-auto transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
            >
              <Ticket className="w-5 h-5" />
              Get Tickets via TicketSpice
            </Button>
          </div>

          {/* Related posts */}
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {blogPosts
              .filter((p) => p.slug !== post.slug)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-pink-500/40 rounded-2xl p-5 transition-all duration-300"
                >
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-2">More reading</p>
                  <h3 className="text-white font-bold mb-1 group-hover:text-pink-400 transition-colors leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-white/60 text-sm">{p.excerpt}</p>
                </Link>
              ))}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
